import { ProjectRenderer } from '../ProjectRenderer';
import { GridHelper, Vector3 } from 'three';
import { ProductModule } from '../objects/ProductModule';
import { RenderUtils } from './RenderUtils';
import RENDERER_CONFIG from '@/configs/rendererConfig';

export class ModulesGrid {
	static readonly LAYER = RENDERER_CONFIG.GRID.LAYER;
	static readonly GRID_UNIT = RENDERER_CONFIG.GRID.GRID_UNIT;
	static readonly BLOCK_HEIGHT = RENDERER_CONFIG.GRID.BLOCK_HEIGHT;
	static readonly HELPER_SIZE = RENDERER_CONFIG.GRID.HELPER_SIZE;

	private occupancyGrid: Map<string, string[]> = new Map();

	constructor(private renderer: ProjectRenderer) {}

	init() {
		if (this.renderer.isDev) this.drawHelper();
	}

	static snapModulePositionToGrid(
		position: Vector3,
		moduleSizeInBlocks: Vector3
	) {
		const gridSize = ModulesGrid.GRID_UNIT;

		const halfOffsetX = moduleSizeInBlocks.x % 2 ? gridSize / 2 : 0;
		const halfOffsetZ = moduleSizeInBlocks.z % 2 ? gridSize / 2 : 0;

		const snappedX =
				Math.round((position.x - halfOffsetX) / gridSize) * gridSize +
				halfOffsetX,
			snappedZ =
				Math.round((position.z - halfOffsetZ) / gridSize) * gridSize +
				halfOffsetZ;

		return new Vector3(snappedX, position.y, snappedZ);
	}

	private getGridCoordsFromWorld(worldPosition: Vector3) {
		const clone = worldPosition.clone();

		RenderUtils.roundVec3ToPrecision(clone, 2);

		const gridX = Math.floor(clone.x / ModulesGrid.GRID_UNIT);
		const gridY = Math.floor(clone.y / ModulesGrid.BLOCK_HEIGHT);
		const gridZ = Math.floor(clone.z / ModulesGrid.GRID_UNIT);

		return new Vector3(gridX, gridY, gridZ);
	}

	private drawHelper() {
		const gridHelper = new GridHelper(
			ModulesGrid.HELPER_SIZE.SIZE * ModulesGrid.GRID_UNIT * 2,
			ModulesGrid.HELPER_SIZE.SIZE * 2,
			0xc9c9c9,
			0xc9c9c9
		);
		gridHelper.layers.set(ModulesGrid.LAYER);

		const levelsQuantity =
			this.renderer.roomManager.roomHeight / ModulesGrid.BLOCK_HEIGHT;

		for (let index = 0; index < levelsQuantity; index++) {
			const helper = gridHelper.clone();
			helper.position.y = index * ModulesGrid.BLOCK_HEIGHT;
			this.renderer.room.add(helper);
		}
	}

	private getRingOffsetsSortedByDistance(radius: number) {
		const offsets: [number, number][] = [];

		for (let x = -radius; x <= radius; x++) {
			for (let z = -radius; z <= radius; z++) {
				if (Math.max(Math.abs(x), Math.abs(z)) !== radius) continue;

				offsets.push([x, z]);
			}
		}

		return offsets.sort(([ax, az], [bx, bz]) => {
			const distanceA = ax * ax + az * az;
			const distanceB = bx * bx + bz * bz;

			if (distanceA !== distanceB) return distanceA - distanceB;
			if (Math.abs(ax) !== Math.abs(bx)) return Math.abs(ax) - Math.abs(bx);
			if (Math.abs(az) !== Math.abs(bz)) return Math.abs(az) - Math.abs(bz);
			if (ax !== bx) return ax - bx;
			return az - bz;
		});
	}

	registerModule(prodModule: ProductModule) {
		this.updateModuleOccupancy(prodModule, true);
	}

	unregisterModule(prodModule: ProductModule) {
		this.updateModuleOccupancy(prodModule, false);
	}

	clear() {
		this.occupancyGrid.clear();
	}

	getModuleGridCoords(prodModule: ProductModule, position?: Vector3) {
		const pos = position || prodModule.position;

		return prodModule.localBlockPositions.map((localBlockPos) => {
			const worldBlockPos = new Vector3(
				pos.x + localBlockPos.x * ModulesGrid.GRID_UNIT,
				pos.y + localBlockPos.y * ModulesGrid.BLOCK_HEIGHT,
				pos.z + localBlockPos.z * ModulesGrid.GRID_UNIT
			);

			return this.getGridCoordsFromWorld(worldBlockPos);
		});
	}

	isPositionValid(prodModule: ProductModule, targetModulePosition: Vector3) {
		for (const gridCoords of this.getModuleGridCoords(
			prodModule,
			targetModulePosition
		)) {
			const key = `${gridCoords.x},${gridCoords.y},${gridCoords.z}`;

			const occupyingModules = this.occupancyGrid.get(key);

			if (occupyingModules && occupyingModules.length > 0) {
				const hasOtherModule = occupyingModules.some(
					(id) => id !== prodModule.id
				);

				if (hasOtherModule) return false;
			}
		}

		return true;
	}

	findNearestAvailablePosition(prodModule: ProductModule, origin?: Vector3) {
		const searchOrigin = origin || new Vector3(0, prodModule.position.y, 0);
		const snappedOrigin = ModulesGrid.snapModulePositionToGrid(
			searchOrigin,
			prodModule.getBoundingBoxInGridUnits()
		);

		if (this.isPositionValid(prodModule, snappedOrigin)) return snappedOrigin;

		for (let radius = 1; ; radius++) {
			const offsets = this.getRingOffsetsSortedByDistance(radius);

			for (const [offsetX, offsetZ] of offsets) {
				const candidatePosition = new Vector3(
					snappedOrigin.x + offsetX * ModulesGrid.GRID_UNIT,
					snappedOrigin.y,
					snappedOrigin.z + offsetZ * ModulesGrid.GRID_UNIT
				);

				if (this.isPositionValid(prodModule, candidatePosition))
					return candidatePosition;
			}
		}
	}

	private updateModuleOccupancy(prodModule: ProductModule, occupy: boolean) {
		for (const localBlockPos of prodModule.localBlockPositions) {
			const worldBlockPos = new Vector3(
				prodModule.position.x + localBlockPos.x * ModulesGrid.GRID_UNIT,
				prodModule.position.y + localBlockPos.y * ModulesGrid.BLOCK_HEIGHT,
				prodModule.position.z + localBlockPos.z * ModulesGrid.GRID_UNIT
			);

			const gridCoords = this.getGridCoordsFromWorld(worldBlockPos);
			const key = `${gridCoords.x},${gridCoords.y},${gridCoords.z}`;

			if (occupy) {
				const existing = this.occupancyGrid.get(key) || [];
				if (!existing.includes(prodModule.id)) {
					existing.push(prodModule.id);
					this.occupancyGrid.set(key, existing);
				}
			} else {
				const existing = this.occupancyGrid.get(key);
				if (existing) {
					const filtered = existing.filter((id) => id !== prodModule.id);
					if (filtered.length > 0) this.occupancyGrid.set(key, filtered);
					else this.occupancyGrid.delete(key);
				}
			}
		}
	}
}
